import React, {useCallback, useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import PropTypes from 'prop-types';
import EditMultiFieldsBuilder from './Shared/EditMultiFieldsBuilder';
import RichEditorResultText, {withSchoolName} from './Shared/RichEditorResultText';
import {optionsShownToRecipient} from './Shared/hiddenOptions';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';

const MultipleChoice = ({
                            index,
                            componentId,
                            fieldKey,
                            value,
                            options,
                            onChange,
                            descriptionHtml,
                            onDescriptionHtmlChange,
                            commitDescriptionHtml,
                            isEditMode,
                            enableValidation,
                            isFormArchived,
                            relatedComponent,
                            readOnly,
                            placeholderValues,
                            accessLevel,
                            error,
                            audience,
                            recipientType,
                            linkedToStudents,
                            linkedToEmployees,
                            submitMode,
                            onlyOneCategory
                        }) => {
    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    const selectedIds = Array.isArray(value) ? value : (value != null && value !== '' ? [value] : []);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const shownOptions = optionsShownToRecipient(options);

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <EditMultiFieldsBuilder
                        componentId={componentId}
                        fieldKey={fieldKey}
                        onChange={onChange}
                        enableValidation={enableValidation}
                        currentDescription={draftDescriptionHtml}
                        setCurrentDescription={handleDraftChange}
                        index={index}
                        options={options}
                        isFormArchived={isFormArchived}
                        relatedComponent={relatedComponent}
                        accessLevel={accessLevel}
                        audience={audience}
                        recipientType={recipientType}
                        linkedToStudents={linkedToStudents}
                        linkedToEmployees={linkedToEmployees}
                        placeholderValues={placeholderValues}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onlyOneCategory={onlyOneCategory}
                    />
                )
                : (
                    <Box>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            linkedToEmployees={linkedToEmployees}
                            submitMode={submitMode}
                        />

                    <FormControl component="fieldset" variant="standard" error={!!error}>
                        {/* AD-2689 (Maria): the answers are indented a little under the question, as on Single Choice. */}
                        <FormGroup sx={{pl: 2}}>
                            {shownOptions.map((option) => (
                                <FormControlLabel
                                    key={`${descriptionHtml}-option-${option.id}`}
                                    // AD-2758 (Maria, 23 October 2026): the tick box is level with the first line of a
                                    // long answer, such as an admissions category, not the middle of it.
                                    sx={{alignItems: 'flex-start', '& .MuiCheckbox-root': {pt: '2px'}}}
                                    control={
                                        <Checkbox
                                            inputProps={{
                                                disabled: readOnly,
                                            }}
                                            size="small"
                                            value={selectedIds.includes(option.id)}
                                            checked={selectedIds.includes(option.id)}
                                            onChange={(e) =>
                                                onChange({
                                                    id: componentId,
                                                    optionValue: e.target.checked,
                                                    optionIndex: option.id,
                                                })
                                            }
                                        />
                                    }
                                    label={withSchoolName(option.description, placeholderValues)}
                                />
                            ))}
                        </FormGroup>
                        <FormHelperText>{error}</FormHelperText>
                    </FormControl>
                </Box>
                )}
        </Box>
    );
};

MultipleChoice.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fieldKey: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    index: PropTypes.number,
    options: PropTypes.array,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    relatedComponent: PropTypes.object,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    submitMode: PropTypes.bool,
    onlyOneCategory: PropTypes.bool
};

export default MultipleChoice;
